<script lang="ts">
	/**
	 * The individual movements the aggregate is made of (Design §13).
	 *
	 * **This is the half that makes the rest of the page trustworthy.** An operator who cannot see
	 * the rows behind a total has to believe it, and a report nobody can check is a report nobody
	 * acts on. The totals shown at the top of this panel come back from the server beside the page
	 * and are computed from the same ledger under the same conditions — so they are the totals of
	 * *everything the filter matches* rather than of the twenty-five rows on screen, and they cannot
	 * disagree with the summary panel above.
	 *
	 * Two details are decisions rather than layout. **Every row links to the thing it happened to** —
	 * the subscription, the order, the customer — because the question after "what is this €22.60"
	 * is always "whose", and a report that cannot be walked out of is a dead end. And **a refund
	 * carries its credit note by number**, which is the document the customer is holding when they
	 * ring up about it.
	 */

	import { Button, Table, TableBody, TableBodyCell, TableBodyRow, TableHead, TableHeadCell } from 'flowbite-svelte';
	import {
		CreditNoteLink,
		EmptyState,
		InfiniteLoadElement,
		Panel,
		RoutedLink,
		SelectField,
		TableRowsPlaceholder
	} from '../../../components';
	import { formatAmount } from '../../../stores/storefront';
	import { formatCalendarDate } from '../../../Utilities';
	import { fetchReportCSV, fetchReportPDF, listReportEvents } from '../../../AppNetwork';
	import { toastError } from '../../../stores/toast';
	import type { ReportEvent, ReportEventKind, ReportQuery, ReportSort } from '../../../AppTypes';

	export let filter: ReportQuery;

	const PAGE_SIZE = 25;

	const KIND_COPY: Record<ReportEventKind, string> = {
		sale: 'Sale',
		renewal: 'Renewal',
		adjustment: 'Adjustment',
		refund: 'Refund',
		chargeback: 'Chargeback'
	};

	const SORTS: { value: ReportSort; name: string }[] = [
		{ value: 'recent', name: 'Newest first' },
		{ value: 'oldest', name: 'Oldest first' },
		{ value: 'largest', name: 'Largest first' },
		{ value: 'smallest', name: 'Smallest first' }
	];

	let events: ReportEvent[] = [];
	let total: number = 0;
	let loading: boolean = true;
	let loadingMore: boolean = false;
	let error: string = '';
	let exporting: boolean = false;
	/** Named rather than a shared flag, so one export's spinner does not disable the other. */
	let exportingPDF: boolean = false;
	let sort: ReportSort = 'recent';
	let sentinel: InfiniteLoadElement | undefined;

	/** Keyed on a string, for the reason `TrendPanel` documents. */
	$: queryKey = JSON.stringify({ filter, sort });
	$: void fnLoad(queryKey);

	async function fnLoad(_key: string): Promise<void> {
		loading = true;
		try {
			const page = await listReportEvents({ ...filter, sort }, PAGE_SIZE, 0);
			events = page.items;
			total = page.total;
			error = '';
		} catch (caught) {
			error = caught instanceof Error ? caught.message : 'Failed to load the movements';
			events = [];
			total = 0;
		} finally {
			loading = false;
			// The sentinel is remeasured after a reset, or an infinite scroll that has already fired
			// once never fires again on the new set.
			sentinel?.updateState();
		}
	}

	async function fnMore(): Promise<void> {
		if (loadingMore || loading || events.length >= total) return;
		loadingMore = true;
		try {
			const page = await listReportEvents({ ...filter, sort }, PAGE_SIZE, events.length);
			events = [...events, ...page.items];
			total = page.total;
		} catch (caught) {
			error = caught instanceof Error ? caught.message : 'Failed to load more movements';
		} finally {
			loadingMore = false;
			sentinel?.updateState();
		}
	}

	/**
	 * The spreadsheet export — the rows as they are, for somebody reconciling *with* them.
	 *
	 * Fetched as a blob rather than linked, because the endpoint needs the bearer token and a token
	 * in a URL is a token in a log. `fnSave` owns the rest, which both exports share.
	 */
	async function fnExport(): Promise<void> {
		exporting = true;
		try {
			await fnSave(await fetchReportCSV({ ...filter, sort }), 'sales-report.csv');
		} catch (caught) {
			toastError('The export could not be downloaded.', (caught as Error).message);
		} finally {
			exporting = false;
		}
	}

	/**
	 * The PDF, through the same save-a-blob path the CSV uses.
	 *
	 * A second format rather than a replacement: the CSV is what somebody reconciles *with*, and the
	 * PDF is what gets filed or forwarded. Its row cap is much lower than the CSV's, because every
	 * row is laid out rather than joined with commas — the document states what it is a part of when
	 * the cap bites, so a short export is never silently short.
	 */
	async function fnExportPDF(): Promise<void> {
		exportingPDF = true;
		try {
			await fnSave(await fetchReportPDF({ ...filter, sort }), 'movements.pdf');
		} catch (caught) {
			toastError('The PDF could not be downloaded.', (caught as Error).message);
		} finally {
			exportingPDF = false;
		}
	}

	/**
	 * Hand a blob to the browser as a download.
	 *
	 * One helper for both exports: the anchor is created, clicked and dropped rather than rendered,
	 * because a permanently visible download link would hold a stale URL from whichever filter was
	 * in force when the panel mounted. Revoked on a delay — Safari cancels a download whose blob has
	 * already been released.
	 */
	async function fnSave(blob: Blob, filename: string): Promise<void> {
		const url = URL.createObjectURL(blob);
		const anchor = document.createElement('a');
		anchor.href = url;
		anchor.download = filename;
		anchor.click();
		setTimeout(() => URL.revokeObjectURL(url), 10000);
	}

	function fnMoney(amount: number, currency: string): string {
		return $formatAmount(amount, currency) ?? `${amount} minor units`;
	}

	/** Where a row goes when it is clicked through. Null when there is nowhere useful to send it. */
	function fnTarget(event: ReportEvent): string | null {
		if (event.subscriptionUUID !== null) {
			return `/Settings/Admin/Transactions/Subscription/${event.subscriptionUUID}`;
		}
		if (event.orderUUID !== null) return `/Settings/Admin/Transactions/Order/${event.orderUUID}`;
		if (event.userUUID !== null) return `/Settings/Admin/Customers/${event.userUUID}`;
		return null;
	}
</script>

<Panel title="Movements" faIcon="list-ol">
	<svelte:fragment slot="title">
		<span class="text-xs text-default">
			Every row the figures above are made of.
		</span>
	</svelte:fragment>

	<svelte:fragment slot="titleRight">
		<label class="text-xs text-default shrink-0" for="reports-sort">Order</label>
		<SelectField id="reports-sort" size="sm" class="w-40" bind:value={sort} items={SORTS} />
		<Button size="xs" color="alternative" disabled={exporting} on:click={() => void fnExport()}>
			{exporting ? 'Preparing…' : 'Export CSV'}
		</Button>
		<Button size="xs" color="alternative" disabled={exportingPDF} on:click={() => void fnExportPDF()}>
			{exportingPDF ? 'Preparing…' : 'Export PDF'}
		</Button>
	</svelte:fragment>

	{#if error.length > 0}
		<EmptyState variant="error" title="The movements could not be loaded." description={error}>
			<Button color="alternative" size="sm" on:click={() => void fnLoad(queryKey)}>Try again</Button>
		</EmptyState>
	{:else if !loading && events.length === 0}
		<EmptyState
			variant="empty"
			title="No movements in this range."
			description="Widen the range, or clear a filter that may be narrowing it to nothing."
		/>
	{:else}
		<p class="text-default text-sm mb-2">
			{total.toLocaleString()} movement{total === 1 ? '' : 's'} match this filter.
		</p>

		<Table divClass="relative overflow-x-auto">
			<TableHead>
				<TableHeadCell>Date</TableHeadCell>
				<TableHeadCell>Movement</TableHeadCell>
				<TableHeadCell>Reference</TableHeadCell>
				<TableHeadCell>Customer</TableHeadCell>
				<TableHeadCell>Product</TableHeadCell>
				<TableHeadCell class="text-right">Amount</TableHeadCell>
				<TableHeadCell class="text-right">Tax</TableHeadCell>
				<TableHeadCell>Credit note</TableHeadCell>
			</TableHead>
			<TableBody>
				{#if loading}
					<TableRowsPlaceholder rows={6} columns={8} />
				{:else}
					{#each events as event (`${event.sourceKind}:${event.sourceUUID}`)}
						{@const target = fnTarget(event)}
						<TableBodyRow>
							<TableBodyCell class="dataTable">
								{formatCalendarDate(event.occurredAt)}
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								<div>{KIND_COPY[event.kind]}</div>
								{#if event.interval !== null}
									<div class="text-xs text-default">
										{event.interval === 'one_time' ? 'One-time' : `Per ${event.interval}`}
									</div>
								{/if}
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								{#if target !== null}
									<!-- Routed, never a bare anchor: a full page load re-runs the AbstractID
									     handshake, and an admin clicking twenty of these would do it twenty
									     times. -->
									<RoutedLink to={target} class="text-primary-500 hover:underline">
										{event.reference}
									</RoutedLink>
								{:else}
									{event.reference}
								{/if}
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								{#if event.userUUID !== null}
									<RoutedLink
										to={`/Settings/Admin/Customers/${event.userUUID}`}
										class="text-primary-500 hover:underline"
									>
										{event.customerName ?? event.customerEmail ?? 'Customer'}
									</RoutedLink>
								{:else}
									{event.customerName ?? event.customerEmail ?? '—'}
								{/if}
								{#if event.country !== null}
									<div class="text-xs text-default">{event.country}</div>
								{/if}
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								<div>{event.productName ?? '—'}</div>
								{#if event.familyName !== null}
									<div class="text-xs text-default">{event.familyName}</div>
								{/if}
							</TableBodyCell>
							<!-- `tabular-nums` for the reason every money column carries it: without it a
							     narrow column may break a total across two lines, and a figure rendered
							     "−€58.0 / 0" still looks like a number. -->
							<TableBodyCell class="dataTable text-right tabular-nums">
								{fnMoney(event.amount, event.currency)}
								<div class="text-xs text-default uppercase">{event.currency}</div>
							</TableBodyCell>
							<TableBodyCell class="dataTable text-right tabular-nums">
								{fnMoney(event.tax, event.currency)}
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								{#if event.creditNoteUUID !== null && event.creditNoteNumber !== null}
									<CreditNoteLink
										uuid={event.creditNoteUUID}
										documentNumber={event.creditNoteNumber}
										label={event.creditNoteNumber}
										admin
									/>
								{:else}
									<span class="text-default">—</span>
								{/if}
							</TableBodyCell>
						</TableBodyRow>
					{/each}
				{/if}
			</TableBody>
		</Table>

		{#if !loading && events.length < total}
			<InfiniteLoadElement
				bind:this={sentinel}
				class="w-full"
				on:becomeVisible={() => void fnMore()}
			>
				{#if loadingMore}
					<EmptyState variant="loading" compact title="Loading more…" />
				{/if}
			</InfiniteLoadElement>
		{/if}
	{/if}
</Panel>
